import * as yup from 'yup';
import { Asserts } from 'yup';

import { clientSchema, NO_SALUTATION_SELECTED } from '@/features/Clients';

import {
  getDay100YearsBefore,
  getDayNYearsBefore,
  getDifference,
} from '@/utils/dates/dates';
import { useInputStore } from '@/features/Sparten';
import { minderjaehrigeKinderSchema } from '@/features/Sparten/shared/schema/kfz/versicherungsnehmerSchema/versicherungsnehmerSchema';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';
import { useGlobalStore } from '@/features/Global/store';
import { WKZ } from '@/features/Sparten/shared/types/kfz/types';
import { isPkwSparte } from '@/features/Sparten/shared/kfz/isPkwSparte';
import { isKradSparte } from '@/features/Sparten/shared/kfz/isKradSparte';
import { getDriverWithSingleUserFlag } from '@/features/Sparten/shared/components/kfz/Fahrerkreis/utils';
import { FAHRERKREIS_TYP } from '@/features/Sparten/shared/types';
import { testSpecialChars } from '@/utils/strings/strings';

export const kfzFahrerMetaSchema = yup.object({
  bezugZumVn: yup
    .string()
    .default('')
    .test({
      name: 'onlyOneEinzelnutzerWhenVnIsCompany',
      message: 'Es darf nur einen Einzelnutzer geben.',
      test: () => {
        if (useInputStore().versicherungsnehmer.isFirma === true) {
          return getDriverWithSingleUserFlag() <= 1;
        }

        return true;
      },
    }),

  isFullAge: yup.boolean().default(false),
  inHaeuslicherGemeinschaftMitVN: yup.boolean().default(false),
  hasAnBegleitetenFahrenTeilgenommen: yup.boolean().default(false),
  hasPunkteInFlensburg: yup.boolean().default(false),
  isHauptnutzer: yup.boolean().default(false),

  // zusätzliche Felder wenn Fahrer von einer Firma
  berufsstatus: yup.string().default(''),
  speziellerArbeitgeber: yup.string().default(''),
  tarifgruppe: yup.string().default(''),
  hasMinderjaehrigeKinder: yup.string().default(''),
  minderjaehrigeKinder: minderjaehrigeKinderSchema,
  hasJahreskarteOeffentlicherPersonenverkehr: yup.boolean().default(false),
  hasFuehrerscheinInEUErworben: yup.boolean().default(false),
  jahreskarteOeffentlicherPersonenverkehr: yup.object({
    bahnNormal: yup.boolean().default(false),
    bahnComfort: yup.boolean().default(false),
    sonstige: yup.boolean().default(false),
  }),
  isMitarbeiterAutohersteller: yup.boolean().default(false),
  isVorsteuerAbzugsberechtigt: yup.boolean().default(false),
  hasAnSicherheitstrainingTeilgenommen: yup.boolean().default(false),
  hasWohnsitzMin5JahreInBrd: yup.boolean().default(false),
  hasWohneigentum: yup.boolean().default(false),
  wohneigentum: yup.object({
    typ: yup.string().default(''),
    gesellschaftID: yup
      .number()
      .default(0)
      .test({
        name: 'isValidValue',
        message: 'Bitte füllen Sie das Pflichtfeld aus',
        test(value, ctx) {
          const isFirma = useInputStore().versicherungsnehmer.isFirma === true;
          // no validation required if vn = company
          if (!isFirma) {
            return true;
          }

          if (
            !('from' in ctx) ||
            !Array.isArray(ctx.from) ||
            ctx.from.length < 1
          ) {
            return true;
          }

          const driverMeta: KfzFahrerResource['meta'] | undefined =
            ctx.from[1].value;

          if (driverMeta === undefined) {
            return false;
          }

          if (
            getSimpleChoiceMeta(
              SIMPLE_CHOICE.BEZUG_ZUM_VN_JURISTISCHE_PERSON,
              SIMPLE_CHOICE_META.BEZUG_ZUM_VN_IS_EINZELNUTZER,
            ).includes(driverMeta.bezugZumVn) &&
            driverMeta.hasWohneigentum === true &&
            (value === 0 || !value)
          ) {
            return false;
          }

          return true;
        },
      }),
    isVermietet: yup.boolean().default(false),
  }),
});

type KfzClientMetaSchema = Asserts<typeof kfzFahrerMetaSchema>;

const validateDriverLicenseDate: (
  value: Date | null,
  ctx: yup.TestContext,
  minAgeCallback: (driverMeta: KfzClientMetaSchema) => number,
) => boolean = (value, ctx, minAgeCallback) => {
  if (!('meta' in ctx.parent)) {
    return true;
  }

  const driverData: KfzFahrerResource | undefined = ctx.parent;

  if (driverData === undefined) {
    return false;
  }

  if (
    value === null ||
    value === undefined ||
    driverData.geburtsdatum === null ||
    driverData.geburtsdatum === undefined
  ) {
    return true;
  }

  return (
    getDifference(value, driverData.geburtsdatum, true) >=
    minAgeCallback(driverData.meta)
  );

  return false;
};

export const kfzClientBaseSchema = clientSchema.concat(
  yup.object({
    anredeId: yup
      .number()
      .default(NO_SALUTATION_SELECTED)
      .when({
        is: () => useInputStore().versicherungsnehmer.isFirma === false,
        then: yup.number().required(),
      }),
    geburtsdatum: yup
      .date()
      .nullable()
      .default(null)
      .when({
        is: () => useInputStore().versicherungsnehmer.isFirma === false,
        then: yup
          .date()
          .min(getDay100YearsBefore())
          .max(getDayNYearsBefore(17))
          .required(),
      }),
  }),
);

export const kfzVersicherungsnehmerSchema = kfzClientBaseSchema.concat(
  yup.object({
    familienstand: yup
      .string()
      .default('')
      .when('meta', {
        is: (meta: KfzClientMetaSchema) => {
          return (
            useInputStore().versicherungsnehmer.isFirma === true &&
            meta !== undefined &&
            getSimpleChoiceMeta(
              SIMPLE_CHOICE.BEZUG_ZUM_VN_JURISTISCHE_PERSON,
              SIMPLE_CHOICE_META.BEZUG_ZUM_VN_IS_EINZELNUTZER,
            ).includes(meta.bezugZumVn)
          );
        },
        then: yup.string().required(),
      }),
    adresse: yup.object({
      plz: yup
        .string()
        .default('')
        .max(5, 'Bitte geben Sie eine gültige Postleitzahl ein')
        .required(),
      ort: yup.string().default('').required(),
    }),
  }),
);

export const kfzFahrerSchema = kfzClientBaseSchema.concat(
  yup.object({
    geburtsdatum: yup
      .date()
      .nullable()
      .default(null)
      .min(getDay100YearsBefore())
      .max(getDayNYearsBefore(17))
      .required(),
    fuehrerscheindatum: yup
      .date()
      .default(null)
      .nullable()
      .when(['meta.isFullAge'], {
        is: (isFullAge: boolean) => !isFullAge,
        then: yup.date().required(),
      })
      .test({
        name: 'isCorrect',
        message: 'Bitte prüfen Sie das Führerscheindatum',
        test: (value, ctx) => {
          return validateDriverLicenseDate(value, ctx, (driverMeta) => {
            const { sparte } = useGlobalStore();
            let minAge = 18;
            if (isKradSparte(sparte)) {
              minAge =
                useInputStore().allgemein.wkz !== WKZ.KRAFTRAEDER_KRAFTROLLER
                  ? 16
                  : 18;
            } else if (isPkwSparte(sparte)) {
              minAge = driverMeta.hasAnBegleitetenFahrenTeilgenommen ? 17 : 18;
            }

            return minAge;
          });
        },
      }),
    meta: kfzFahrerMetaSchema,
  }),
);

export type KfzFahrerResource = Asserts<typeof kfzFahrerSchema>;

export const kfzFahrerContractSchema = kfzFahrerSchema.concat(
  yup.object({
    vorname: yup
      .string()
      .default('')
      .when({
        is: () =>
          useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.FESTGELEGT,
        then: yup.string().required(),
      })
      .test(testSpecialChars),
    nachname: yup
      .string()
      .default('')
      .when({
        is: () =>
          useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.FESTGELEGT,
        then: yup.string().required(),
      })
      .test(testSpecialChars),
  }),
);

export type KfzFahrerContractResource = Asserts<typeof kfzFahrerContractSchema>;
